import React, { useState } from 'react'
import { Box, SxProps, TextFieldProps, Theme } from '@mui/material'
import { useField } from 'formik'
import { useAppTranslations } from '../../utils/hooks/useAppTranslations'
import dayjs, { Dayjs } from 'dayjs'
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker'
import { EditionModes } from '../../utils/providers/editionModeContext'
import HelperTooltip from './HelperTooltip'
import { compactFormFieldSx } from './DisplayedItem'

const outlinedDateFieldLabelSx = {
  '& .MuiInputLabel-root.MuiInputLabel-shrink': {
    backgroundColor: 'transparent',
  },
} as const

export interface DateFieldProps {
  name: string
  label: string
  inputFormat?: string
  valueFormat?: string
  minDate?: Dayjs
  maxDate?: Dayjs
  margin?: TextFieldProps['margin']
  required?: boolean
  InputProps?: TextFieldProps['InputProps']
  InputLabelProps?: TextFieldProps['InputLabelProps']
  onMouseLeave?: () => void
  sx?: SxProps<Theme>
  disableHoverEdition?: boolean
  setEditionMode?: React.Dispatch<React.SetStateAction<EditionModes>>
  tooltipText?: string
  variant?: 'filled' | 'outlined' | 'standard'
  placeholder?: string
  defaultHelperText?: string
  clearable?: boolean
  views?: Array<'day' | 'month' | 'year'>
  disableFuture?: boolean
  compact?: boolean
}

const DateField: React.FC<DateFieldProps> = ({
  name,
  label,
  margin = 'normal',
  inputFormat = 'DD/MM/YYYY',
  valueFormat,
  onMouseLeave,
  sx,
  disableHoverEdition = false,
  setEditionMode,
  tooltipText,
  variant = 'standard',
  defaultHelperText = '',
  placeholder,
  clearable = false,
  views,
  InputLabelProps,
  InputProps,
  compact = false,
  required = false,
  ...props
}) => {
  const t = useAppTranslations()
  const [field, meta, helpers] = useField({ name })
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const hasError = Boolean(meta.error)
  const helperText = (meta.error && hasError && t(meta.error)) || defaultHelperText

  const isSame = (date?: string): boolean => dayjs(date).diff(dayjs(meta.initialValue)) === 0

  const canChangeEditionMode = !disableHoverEdition && setEditionMode

  const handleDateChange = (date: Dayjs | null): void => {
    if (date?.isValid()) {
      const nextValue = date.format(valueFormat)
      helpers.setValue(nextValue)
      isSame(nextValue) && canChangeEditionMode && setEditionMode(EditionModes.Display)
    } else helpers.setValue(null)
  }

  const placeholderText: string = dayjs(placeholder).isValid()
    ? dayjs(placeholder).format(inputFormat)
    : (placeholder ?? 'n/a')

  return (
    <Box onMouseLeave={() => !isDialogOpen && onMouseLeave && onMouseLeave()}>
      <MobileDatePicker
        format={inputFormat}
        views={views}
        slotProps={{
          field: { clearable },
          textField: {
            slotProps: {
              inputLabel: compact
                ? { sx: { display: 'none' }, ...InputLabelProps }
                : { shrink: true, ...InputLabelProps },
              input: InputProps,
            },
            variant,
            required,
            error: hasError,
            margin: compact ? 'none' : margin,
            helperText,
            'data-testid': 'DateFieldTestId',
            size: 'small',
            fullWidth: true,
            hiddenLabel: compact,
            sx: compact
              ? { ...compactFormFieldSx, ...sx }
              : variant === 'outlined'
                ? [outlinedDateFieldLabelSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]
                : sx,
            placeholder: placeholderText,
          },
        }}
        label={
          <>
            {t(label)}
            {!compact && tooltipText && (
              <HelperTooltip helperText={tooltipText} sx={{ fontSize: 15, marginLeft: '6.5px' }} />
            )}
          </>
        }
        value={field.value ? dayjs(field.value) : null}
        onChange={handleDateChange}
        disableCloseOnSelect={false}
        onOpen={() => setIsDialogOpen(true)}
        onClose={() => setIsDialogOpen(false)}
        {...props}
      />
    </Box>
  )
}

export default DateField
